/**
 * Агрегация для графиков раздела Usage & Billing.
 *
 * Отдельного ряда по времени контракт не отдаёт: есть постраничный список
 * записей и отдельный агрегат `totals`. Поэтому графики строятся по загруженной
 * странице — и это надо говорить в интерфейсе прямо, иначе столбик за день
 * прочитают как расход за весь период.
 *
 * Здесь только арифметика, без React: её можно проверить тестами, а не глазами.
 */

export interface RankedSlice {
  key: string;
  label: string;
  total: number;
  cached: number;
  cost: number;
  records: number;
  /** Доля от суммы всех долей, 0..1. */
  share: number;
}

export interface DayPoint {
  /** `YYYY-MM-DD` в UTC — тем же днём, каким запись попадает в фильтр периода. */
  day: string;
  total: number;
  cost: number;
  records: number;
}

const num = (value: unknown): number =>
  typeof value === 'number' && Number.isFinite(value) ? value : 0;

interface Source {
  total_tokens?: number;
  cached_tokens?: number;
  total_cost?: number;
  records_count?: number;
}

/**
 * Сворачивает записи в ранжированный список долей.
 *
 * Хвост за пределами `limit` не выбрасывается, а собирается в «Other»: сумма
 * видимых столбиков должна сходиться с итогом, иначе график врёт.
 */
export function rank<T extends Source>(
  items: T[] | null | undefined,
  keyOf: (item: T) => string,
  labelOf: (item: T) => string,
  limit = 6
): RankedSlice[] {
  if (!Array.isArray(items) || items.length === 0) return [];

  const byKey = new Map<string, RankedSlice>();
  for (const item of items) {
    if (!item || typeof item !== 'object') continue;
    const key = keyOf(item);
    const prev = byKey.get(key);
    if (prev) {
      prev.total += num(item.total_tokens);
      prev.cached += num(item.cached_tokens);
      prev.cost += num(item.total_cost);
      prev.records += num(item.records_count);
      continue;
    }
    byKey.set(key, {
      key,
      label: labelOf(item),
      total: num(item.total_tokens),
      cached: num(item.cached_tokens),
      cost: num(item.total_cost),
      records: num(item.records_count),
      share: 0,
    });
  }

  const ordered = Array.from(byKey.values()).sort((a, b) => b.total - a.total);
  const head = ordered.slice(0, Math.max(1, limit));
  const tail = ordered.slice(Math.max(1, limit));

  if (tail.length > 0) {
    head.push(
      tail.reduce(
        (acc, slice) => ({
          key: '__other__',
          label: `Other (${tail.length})`,
          total: acc.total + slice.total,
          cached: acc.cached + slice.cached,
          cost: acc.cost + slice.cost,
          records: acc.records + slice.records,
          share: 0,
        }),
        { key: '__other__', label: '', total: 0, cached: 0, cost: 0, records: 0, share: 0 }
      )
    );
  }

  const sum = head.reduce((acc, slice) => acc + slice.total, 0);
  return head.map((slice) => ({ ...slice, share: sum > 0 ? slice.total / sum : 0 }));
}

/** Сколько дней подряд готовы нарисовать: дальше столбики становятся нечитаемой щёткой. */
export const MAX_TIMELINE_DAYS = 62;

/**
 * Расход по календарным дням.
 *
 * Пропущенные дни заполняются нулями: разрыв в данных и нулевой расход — разные
 * вещи, но на графике времени обе читаются как «в этот день ничего не было»,
 * а вот сжатая ось без пропусков искажает темп.
 */
export function dailySeries(
  items: Array<{ created_at?: string | null } & Source> | null | undefined
): DayPoint[] {
  if (!Array.isArray(items) || items.length === 0) return [];

  const byDay = new Map<string, DayPoint>();
  for (const item of items) {
    const day = utcDay(item?.created_at);
    if (!day) continue;
    const prev = byDay.get(day);
    if (prev) {
      prev.total += num(item.total_tokens);
      prev.cost += num(item.total_cost);
      prev.records += num(item.records_count);
      continue;
    }
    byDay.set(day, {
      day,
      total: num(item.total_tokens),
      cost: num(item.total_cost),
      records: num(item.records_count),
    });
  }

  const days = Array.from(byDay.keys()).sort();
  if (days.length === 0) return [];

  const first = Date.parse(`${days[0]}T00:00:00.000Z`);
  const last = Date.parse(`${days[days.length - 1]}T00:00:00.000Z`);
  const span = Math.round((last - first) / 86_400_000) + 1;
  if (span > MAX_TIMELINE_DAYS) {
    return days.map((day) => byDay.get(day) as DayPoint);
  }

  const filled: DayPoint[] = [];
  for (let i = 0; i < span; i += 1) {
    const day = new Date(first + i * 86_400_000).toISOString().slice(0, 10);
    filled.push(byDay.get(day) ?? { day, total: 0, cost: 0, records: 0 });
  }
  return filled;
}

function utcDay(value: string | null | undefined): string | null {
  if (typeof value !== 'string' || !value.trim()) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toISOString().slice(0, 10);
}

/** Короткая подпись числа для оси: 1 200 000 → «1.2M». */
export function compactNumber(value: number): string {
  const abs = Math.abs(value);
  if (abs >= 1_000_000_000) return `${(value / 1_000_000_000).toFixed(1)}B`;
  if (abs >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${(value / 1_000).toFixed(abs >= 10_000 ? 0 : 1)}K`;
  return String(Math.round(value));
}
